import { ErrorModal, TreeNode, TreePickerControlV2 } from "@dexteel/mesf-core";
import {
	CircularProgress,
	FormControl,
	FormHelperText,
	TextField,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { FieldError } from "react-hook-form";
import { DelayCode, TreeCode } from "../models/DelayCode";
import { getDelayCodes } from "../repositories/DelayCodeRepository";

const buildTreeCodes = (nodes: TreeCode[]): TreeNode | null => {
	//tree build functions
	const idMapping = nodes.reduce((acc: any, el: TreeCode, i: number) => {
		acc[el.id] = i;
		return acc;
	}, {});

	const roots: TreeCode[] = [];

	nodes.forEach((el: TreeCode) => {
		// Handle root elements (no parent)
		if (el.parentId === null) {
			roots.push(el);
			return;
		}
		// Use our mapping to locate the parent element in our data array
		const parentEl = nodes[idMapping[el.parentId]];
		// Add our current el to its parent's `children` array
		if (parentEl) {
			parentEl.children = [...(parentEl?.children || []), el];
		}
	});

	// If multiple roots, create a virtual root to contain them all
	if (roots.length > 1) {
		return {
			id: 0,
			parentId: null,
			name: "All",
			isLeaf: false,
			isActive: true,
			isSelectable: false,
			children: roots,
			sortOrder: 0,
		} as unknown as TreeNode;
	}

	return (roots[0] ?? null) as unknown as TreeNode;
};

const useDelayCodes = (
	assetId: number | null,
	onError?: (error: Error) => void,
) => {
	return useQuery<DelayCode[], Error>({
		queryKey: ["delayCodes", assetId],
		queryFn: () => getDelayCodes(assetId),
		onError: onError,
	});
};

export const DelayCodeTreePicker = ({
	label = "Code",
	value,
	onChange,
	disabled,
	assetId,
	fieldError,
	showFullPath = false,
}: {
	label?: String;
	value: number | null;
	onChange: (value: number | null, defaultCategoryId: number | null) => void;
	disabled?: boolean;
	assetId: number | null;
	fieldError?: FieldError | undefined;
	showFullPath?: boolean;
}) => {
	const [error, setError] = useState("");

	const { data, isLoading } = useDelayCodes(assetId, (error) =>
		setError(error.message),
	);

	const rowsMapped = (Array.isArray(data) ? data : []).map(
		(del: DelayCode) =>
			({
				id: del.DelayCodeId,
				parentId: del.ParentDelayCodeId,
				name: del.DelayCodeName,
				isLeaf: del.IsLeaf,
				isActive: true,
				isSelectable: del.IsSelectable,
				children: [],
				sortOrder: del.SortOrder,
			}) as TreeCode,
	);

	// Empty tree (line without configured codes) → informational root, never a
	// committable "All" that would send DelayCodeId 0 to the SP.
	const delayCodes =
		buildTreeCodes(rowsMapped || []) ??
		({
			id: 0,
			parentId: null,
			name: "No codes available",
			isLeaf: false,
			isActive: true,
			isSelectable: false,
			children: [],
			sortOrder: 0,
		} as unknown as TreeNode);

	if (isLoading) {
		return (
			<TextField
				disabled
				label="Code"
				InputProps={{
					endAdornment: <CircularProgress color="inherit" size={20} />,
				}}
			/>
		);
	} else {
		return (
			<>
				<FormControl error={!!fieldError} fullWidth sx={{}}>
					<TreePickerControlV2
						inputTitle={label}
						title={label}
						dataSource={delayCodes}
						value={value || 0}
						onSelect={(selected: number) => {
							// 0 is the picker's empty/virtual-root sentinel (clear button,
							// most-used chips on the virtual root) — never a real code.
							const codeId = selected || null;
							const defaultCategoryId =
								(Array.isArray(data) ? data : []).find(
									(c) => c.DelayCodeId === codeId,
								)?.DelayCategoryId ?? null;
							onChange(codeId, defaultCategoryId);
						}}
						selectBranch
						selectActive={false}
						selectInternal={false}
						disabled={disabled}
						showClearButton
						showFullPath={showFullPath}
						mostUsedCount={5}
						itemsWithParent={[
							"F1",
							"F2",
							"F3",
							"F4",
							"F5",
							"F6",
							"F7",
							"R1",
							"R2",
							"R3",
							"R4",
						]}
						dialogMaxWidth="md"
					/>
					{fieldError && <FormHelperText>{fieldError.message}</FormHelperText>}
				</FormControl>
				<ErrorModal error={error} onHide={() => setError("")} />
			</>
		);
	}
};
